/**
 * @fileoverview Assignee picker offering workflow roles and org members (MOR-748)
 *
 * Extends the template-item member picker with the firm's workflow roles: a
 * task can be assigned to a role (Partner, Manager, Analyst, …) that resolves
 * to a person per job, or to a fixed user. Roles are listed first — they're
 * the reason a single workflow can serve every team. Stores either a role id
 * or a user id (never both); a stale user id renders the same greyed
 * "removed" state as `MemberSelect`.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Check, ChevronDown, UserPlus } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { UserAvatar, getDisplayName } from "@/ui/composed";
import { cn } from "@/utils/misc";
import { RoleBadge } from "./RoleBadge";

export interface MemberOrRoleValue {
  userId: Id<"users"> | null;
  roleId: Id<"workflowRoles"> | null;
}

export function MemberOrRoleSelect({
  orgId,
  value,
  onChange,
  placeholder = "Unassigned",
}: {
  orgId: Id<"organizations">;
  value: MemberOrRoleValue;
  onChange: (next: MemberOrRoleValue) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const { data: members } = useQuery(
    convexQuery(api.organizations.queries.getOrganizationMembers, { orgId }),
  );
  const { data: roles } = useQuery(
    convexQuery(api.tasks.workflowRoles.listByOrg, { orgId }),
  );

  const currentUser = useMemo(
    () => (members ?? []).find((m) => m.user._id === value.userId)?.user,
    [members, value.userId],
  );
  const currentRole = useMemo(
    () => (roles ?? []).find((r) => r._id === value.roleId),
    [roles, value.roleId],
  );

  // Stored ids that no longer resolve → greyed "removed" states.
  const missingUser =
    value.userId !== null && members !== undefined && !currentUser;
  const missingRole =
    value.roleId !== null && roles !== undefined && !currentRole;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-2.5 text-sm",
            "transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          )}
        >
          {currentRole ? (
            <RoleBadge role={currentRole} className="shrink-0" />
          ) : currentUser ? (
            <UserAvatar
              avatarUrl={currentUser.avatarUrl}
              firstName={currentUser.firstName}
              lastName={currentUser.lastName}
              email={currentUser.email}
              className="size-5 shrink-0 text-[9px]"
            />
          ) : (
            <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-dashed border-foreground/30 text-foreground/50">
              <UserPlus className="size-3" />
            </span>
          )}
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-left",
              missingUser || missingRole
                ? "italic text-muted-foreground"
                : currentUser || currentRole
                  ? "text-foreground"
                  : "text-muted-foreground",
            )}
          >
            {missingUser
              ? "Removed user — will be skipped"
              : missingRole
                ? "Removed role — will be skipped"
                : currentRole
                  ? "Resolved per job"
                  : currentUser
                    ? getDisplayName(
                        currentUser.firstName,
                        currentUser.lastName,
                        currentUser.email,
                      )
                    : placeholder}
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search roles or members…"
            className="h-9 text-xs"
          />
          <CommandList>
            <CommandEmpty>Nothing found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="Unassigned"
                onSelect={() => {
                  onChange({ userId: null, roleId: null });
                  setOpen(false);
                }}
                className="text-xs"
              >
                <Check
                  className={cn(
                    "mr-2 size-3.5",
                    !value.userId && !value.roleId
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                {placeholder}
              </CommandItem>
            </CommandGroup>
            {(roles ?? []).length > 0 && (
              <CommandGroup heading="Roles — resolved per job">
                {(roles ?? []).map((role) => (
                  <CommandItem
                    key={role._id}
                    value={`role ${role.name}`}
                    onSelect={() => {
                      onChange({ userId: null, roleId: role._id });
                      setOpen(false);
                    }}
                    className="gap-2 text-xs"
                  >
                    <Check
                      className={cn(
                        "size-3.5",
                        value.roleId === role._id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <RoleBadge role={role} />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            <CommandGroup heading="Members">
              {(members ?? []).map((m) => {
                const name = getDisplayName(
                  m.user.firstName,
                  m.user.lastName,
                  m.user.email,
                );
                return (
                  <CommandItem
                    key={m.user._id}
                    value={name}
                    onSelect={() => {
                      onChange({ userId: m.user._id, roleId: null });
                      setOpen(false);
                    }}
                    className="gap-2 text-xs"
                  >
                    <Check
                      className={cn(
                        "size-3.5",
                        value.userId === m.user._id
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    <UserAvatar
                      avatarUrl={m.user.avatarUrl}
                      firstName={m.user.firstName}
                      lastName={m.user.lastName}
                      email={m.user.email}
                      className="size-5 text-[9px]"
                    />
                    <span className="truncate">{name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
